// The Medusa Room — Web Audio engine
// Generative dark-trap stem sequencer (drums / bass / melody / vox pad) + ambient atmosphere.
// Everything is synthesized live: no audio files needed for the interactive studio.

export class MedusaEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.analyser = null;
    this.stemGains = {};
    this.stemState = { drums: true, bass: true, melody: true, vox: true };
    this.schedulerId = null;
    this.step = 0;
    this.nextStepTime = 0;
    this.playing = false;
    this.ambientNodes = [];
    this.ambientOn = false;
    this.bpm = 138;
  }

  ensureCtx() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.9;
      // Venom FX chain: master → lowpass → analyser → destination; reverb tapped off master
      this.lowpass = this.ctx.createBiquadFilter();
      this.lowpass.type = "lowpass";
      this.lowpass.frequency.value = 20000;
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 2048;
      this.master.connect(this.lowpass);
      this.lowpass.connect(this.analyser);
      this.analyser.connect(this.ctx.destination);
      // dungeon reverb — synthesized impulse response (decaying noise), parallel wet path
      const irLen = this.ctx.sampleRate * 2.2;
      const ir = this.ctx.createBuffer(2, irLen, this.ctx.sampleRate);
      for (let c = 0; c < 2; c++) {
        const d = ir.getChannelData(c);
        for (let i = 0; i < irLen; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / irLen, 2.4);
      }
      this.verb = this.ctx.createConvolver();
      this.verb.buffer = ir;
      this.verbWet = this.ctx.createGain();
      this.verbWet.gain.value = 0; // off until Dungeon Reverb is lit
      this.master.connect(this.verb);
      this.verb.connect(this.verbWet);
      this.verbWet.connect(this.lowpass);
      this.fxState = { reverb: false, lowpass: false };
      for (const name of ["drums", "bass", "melody", "vox"]) {
        const g = this.ctx.createGain();
        g.gain.value = 1;
        g.connect(this.master);
        this.stemGains[name] = g;
      }
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
    return this.ctx;
  }

  // Venom FX pad
  toggleReverb(on) {
    this.ensureCtx();
    this.fxState.reverb = on;
    this.verbWet.gain.setTargetAtTime(on ? 0.5 : 0, this.ctx.currentTime, 0.08);
  }

  toggleLowpass(on) {
    this.ensureCtx();
    this.fxState.lowpass = on;
    this.lowpass.frequency.setTargetAtTime(on ? 520 : 20000, this.ctx.currentTime, 0.06);
  }

  tapeStop() {
    // momentary pitch-down-drain then recover — classic tape stop
    const ctx = this.ensureCtx();
    const t = ctx.currentTime;
    this.lowpass.frequency.cancelScheduledValues(t);
    this.lowpass.frequency.setValueAtTime(this.fxState.lowpass ? 520 : 20000, t);
    this.lowpass.frequency.exponentialRampToValueAtTime(90, t + 0.55);
    this.lowpass.frequency.exponentialRampToValueAtTime(this.fxState.lowpass ? 520 : 20000, t + 1.1);
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setValueAtTime(0.9, t);
    this.master.gain.exponentialRampToValueAtTime(0.12, t + 0.55);
    this.master.gain.exponentialRampToValueAtTime(0.9, t + 1.1);
  }

  setStem(name, on) {
    this.stemState[name] = on;
    const g = this.stemGains[name];
    if (g && this.ctx) g.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, 0.03);
  }

  // ---------------- generative stem loop ----------------
  _kick(t, out) {
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.frequency.setValueAtTime(150, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.12);
    g.gain.setValueAtTime(0.9, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
    o.connect(g); g.connect(out);
    o.start(t); o.stop(t + 0.3);
  }

  _hat(t, out, open = false) {
    const len = open ? 0.12 : 0.03;
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = "highpass"; f.frequency.value = 7000;
    const g = this.ctx.createGain();
    g.gain.value = 0.16;
    src.connect(f); f.connect(g); g.connect(out);
    src.start(t);
  }

  _snare(t, out) {
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.18, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.6);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = "bandpass"; f.frequency.value = 1900; f.Q.value = 0.9;
    const g = this.ctx.createGain();
    g.gain.value = 0.4;
    src.connect(f); f.connect(g); g.connect(out);
    src.start(t);
  }

  _bass(t, out, freq) {
    const o = this.ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.5, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    const dist = this.ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) { const x = i / 128 - 1; curve[i] = Math.tanh(2.2 * x); }
    dist.curve = curve;
    o.connect(dist); dist.connect(g); g.connect(out);
    o.start(t); o.stop(t + 0.55);
  }

  _pluck(t, out, freq) {
    const o = this.ctx.createOscillator();
    o.type = "triangle";
    o.frequency.value = freq;
    const f = this.ctx.createBiquadFilter();
    f.type = "lowpass"; f.frequency.value = 2400;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.22, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.34);
    o.connect(f); f.connect(g); g.connect(out);
    o.start(t); o.stop(t + 0.4);
  }

  _padChord(t, out, freqs, dur) {
    freqs.forEach((fq) => {
      const o = this.ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = fq * (1 + (Math.random() - 0.5) * 0.004);
      const f = this.ctx.createBiquadFilter();
      f.type = "lowpass"; f.frequency.value = 620; f.Q.value = 2;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.028, t + dur * 0.4);
      g.gain.linearRampToValueAtTime(0.0001, t + dur);
      o.connect(f); f.connect(g); g.connect(out);
      o.start(t); o.stop(t + dur + 0.05);
    });
  }

  startStems() {
    const ctx = this.ensureCtx();
    if (this.playing) return;
    this.playing = true;
    const stepDur = 60 / this.bpm / 2; // 8th notes
    const bassLine = [55, 55, 65.41, 49, 55, 55, 73.42, 65.41]; // A1 A1 C2 G1 A1 A1 D2 C2
    const melodyNotes = [220, 0, 261.63, 0, 329.63, 0, 261.63, 293.66, 220, 0, 196, 0, 329.63, 440, 392, 329.63];
    const chords = [[110, 130.81, 164.81], [87.31, 130.81, 174.61]]; // Am / F
    this.step = 0;
    this.nextStepTime = ctx.currentTime + 0.05;

    const tick = () => {
      if (!this.playing) return;
      while (this.nextStepTime < ctx.currentTime + 0.18) {
        const t = this.nextStepTime;
        const s = this.step;
        const d = this.stemGains.drums, b = this.stemGains.bass, m = this.stemGains.melody, v = this.stemGains.vox;
        // drums — 16-step trap feel
        if ([0, 7, 10].includes(s % 16)) this._kick(t, d);
        if (s % 2 === 0) this._hat(t, d, s % 8 === 6);
        if (s % 16 === 8) this._snare(t, d);
        // bass
        if (s % 2 === 0) this._bass(t, b, bassLine[(s / 2) % 8]);
        // melody
        const mn = melodyNotes[s % 16];
        if (mn) this._pluck(t, m, mn);
        // vox pad — chord every 16 steps
        if (s % 16 === 0) this._padChord(t, v, chords[Math.floor(s / 16) % 2], stepDur * 16);
        this.step++;
        this.nextStepTime += stepDur;
      }
      this.schedulerId = setTimeout(tick, 60);
    };
    tick();
  }

  stopStems() {
    this.playing = false;
    if (this.schedulerId) clearTimeout(this.schedulerId);
    this.schedulerId = null;
  }

  // ---------------- ambient atmosphere ----------------
  startAmbient() {
    const ctx = this.ensureCtx();
    if (this.ambientOn) return;
    this.ambientOn = true;
    const nodes = [];
    // dark pad — detuned lows through a slow-breathing filter
    const padGain = ctx.createGain();
    padGain.gain.value = 0.05;
    const filt = ctx.createBiquadFilter();
    filt.type = "lowpass"; filt.frequency.value = 300; filt.Q.value = 4;
    [55, 55.4, 82.4, 110.2].forEach((fq) => {
      const o = ctx.createOscillator();
      o.type = "sine"; o.frequency.value = fq;
      o.connect(filt); o.start();
      nodes.push(o);
    });
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 140;
    lfo.connect(lfoGain); lfoGain.connect(filt.frequency);
    lfo.start();
    nodes.push(lfo);
    filt.connect(padGain);
    padGain.connect(this.master);
    nodes.push(filt, padGain);
    // echoing rattle — sparse tremolo noise bursts
    const rattleTimer = setInterval(() => {
      if (!this.ambientOn) return;
      const t = ctx.currentTime;
      const dur = 0.5 + Math.random() * 0.4;
      const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) {
        const trem = Math.abs(Math.sin((i / ctx.sampleRate) * 55 * Math.PI));
        d[i] = (Math.random() * 2 - 1) * trem * (1 - i / d.length) * 0.5;
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass"; bp.frequency.value = 2600; bp.Q.value = 8;
      const g = ctx.createGain();
      g.gain.value = 0.028;
      src.connect(bp); bp.connect(g); g.connect(this.master);
      src.start(t);
    }, 5200);
    this.ambientNodes = nodes;
    this._rattleTimer = rattleTimer;
  }

  stopAmbient() {
    this.ambientOn = false;
    if (this._rattleTimer) clearInterval(this._rattleTimer);
    this.ambientNodes.forEach((n) => {
      try { n.stop ? n.stop() : n.disconnect(); } catch { /* already stopped */ }
    });
    this.ambientNodes = [];
  }
}

// Encode a recorded MediaRecorder blob to a 16-bit mono WAV (backend accepts wav).
export async function blobToWavFile(blob, filename = "snake-pit-idea.wav") {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const arr = await blob.arrayBuffer();
  const audio = await ctx.decodeAudioData(arr);
  const ch = audio.getChannelData(0);
  const len = ch.length;
  const buf = new ArrayBuffer(44 + len * 2);
  const view = new DataView(buf);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  writeStr(0, "RIFF"); view.setUint32(4, 36 + len * 2, true); writeStr(8, "WAVE");
  writeStr(12, "fmt "); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
  view.setUint16(22, 1, true); view.setUint32(24, audio.sampleRate, true);
  view.setUint32(28, audio.sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  writeStr(36, "data"); view.setUint32(40, len * 2, true);
  for (let i = 0; i < len; i++) {
    const s = Math.max(-1, Math.min(1, ch[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  ctx.close();
  return new File([buf], filename, { type: "audio/wav" });
}
